import { CheckListItem } from '$tablet-app/src/app/models';
import { useMainDrawerNavigation } from '$tablet-app/src/app/navigation/hooks';
import { palette } from '$tablet-app/src/app/theme';
import {
  ChecklistModel,
  getChecklistItemDisplayText,
} from '@akin-home/checklists';
import { Text } from '@akin-home/text/native';
import CheckBox from 'expo-checkbox';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { Pressable, SectionList, StyleSheet } from 'react-native';
import styled from 'styled-components/native';

interface ChecklistProps {
  checklist: ChecklistModel[];
  onPress: (checklist: ChecklistModel, item: CheckListItem) => void;
  listEmptyComponent?: React.ReactElement;
}

const Checklist: FC<ChecklistProps> = (props) => {
  const { checklist } = props;

  const data = useMemo(
    () =>
      checklist.map((item) => ({
        item,
        title: item.title,
        data: item.checklist,
      })),
    [checklist]
  );

  const onPress = (checklist: ChecklistModel) => (item: CheckListItem) =>
    props.onPress(checklist, item);

  const { navigate } = useMainDrawerNavigation();
  const openLists = () => navigate('Tasks');

  return (
    <SectionList
      contentContainerStyle={{ flexGrow: 1 }}
      sections={data}
      keyExtractor={(item) => item.id}
      ListEmptyComponent={props.listEmptyComponent}
      ItemSeparatorComponent={ItemSeparatorComponent}
      renderSectionHeader={({ section }) => (
        <Pressable
          style={({ pressed }) => ({ opacity: pressed ? 0.5 : 1 })}
          onPress={openLists}
        >
          <Text weight="bold">{section.title}</Text>
        </Pressable>
      )}
      renderItem={({ item, section }) => (
        <ChecklistItem item={item} onPress={onPress(section.item)} />
      )}
    />
  );
};

interface ChecklistItemProps {
  item: CheckListItem;
  onPress: (item: CheckListItem) => void;
}

const ChecklistItem: FC<ChecklistItemProps> = (props) => {
  const { item, onPress } = props;

  const [checked, setChecked] = useState(item.checked);

  const onLinePress = () => {
    onChange(!checked);
  };

  const onChange = (new_value: boolean) => {
    setChecked(new_value);
    onPress({ ...item, checked: new_value });
  };

  useEffect(() => {
    setChecked(item.checked);
  }, [item.checked]);

  return (
    <LineRow onPress={onLinePress}>
      <CheckBox
        color={checked ? palette.primary_700 : 'lightgrey'}
        value={checked}
        onValueChange={onChange}
      />

      <Text variant="p" style={checked ? styles.strikethrough : undefined}>
        {getChecklistItemDisplayText(item)}
      </Text>
    </LineRow>
  );
};

const LineRow = styled.TouchableOpacity`
  flex-direction: row;
  align-items: center;
  padding: 14px 0;
  gap: 20px;
`;

const ItemSeparatorComponent = styled.View`
  width: 100%;
  border-bottom-width: 1px;
  border-style: dashed;
  border-bottom-color: lightgrey;
`;

// Styles
const styles = StyleSheet.create({
  strikethrough: {
    textDecorationLine: 'line-through',
    textDecorationStyle: 'solid',
    color: palette.dim,
  },
});

export default Checklist;
